/**
 * common functions for "Monitoring Tools" pages
 * providing methods for dealing with "station" data
 * on the map, and in the vuex store
 */
import Vue from 'vue';
import mapboxgl from 'mapbox-gl';
import * as appColors from '@/functions/appColors';
import labelLayer from '@/functions/labelLayer';
// import getUniqueFeatures from '@/functions/getUniqueFeatures';
import { apolloClient } from '@/apollo/apollo';
import gql from 'graphql-tag';

// local modules
import * as actions from '@/constants/actions';
import saveMapPosition from '@/mixins/saveMapPosition';
import mapPromiseMixin from '@/mixins/map';
import overlapPickerMixin from '@/mixins/overlapPicker';
import revealFeatureMixin from '@/mixins/revealFeature';
import lookupMixin from '@/mixins/lookupGeojsonProperties';
import mapStatePositionMixin from '@/mixins/mapStatePosition';

// components
import MapContextMenu from '@/components/MapContextMenu';
import MapboxGlMap from '@/components/MapboxGlMap';
import StyleSwitcher from '@/components/MapStyleSwitcher';
import MapSidebar from '@/components/StationsPageSidebar';

export default {
    components: {
        MapboxGlMap,
        StyleSwitcher,
        MapSidebar,
    },
    mixins: [
        saveMapPosition,
        mapPromiseMixin,
        overlapPickerMixin,
        revealFeatureMixin,
        lookupMixin,
        mapStatePositionMixin,
    ],
    data() {
        return {
            mapZoom: 5.8,
            tooltipMarker: null,
            contextMenu: null,
            mapLayersReady: false,
            // station layer ids (so we can do loop operations on all layers)
            mapLayers: [],
            // map filters
            filters: [],
            showStyleSwitcher: false,
        };
    },
    computed: {
        showingSidebar() {
            return this.$store.state.sidebar.show;
        },
        classObj() {
            return [
                {
                    'showing-sidebar': this.showingSidebar,
                },
            ];
        },
        // selected station feature
        selectedStation() {
            return this.$store.state.map.selectedStation;
        },
        filterSettings() {
            return this.$store.state.filterSettings;
        },
    },
    beforeRouteLeave(to, from, next) {
        // unload and reset map filters
        this.$store.dispatch(actions.UNLOAD_FILTERS);
        // reset sidebar & switch back to the live list view
        this.$store.dispatch(actions.SHOW_MAP_SIDEBAR, false);
        this.$store.dispatch(actions.SET_SIDEBAR_CONTEXT, 'live');
        // reset sidebar context and selected features
        this.$store.dispatch(actions.SELECT_STATION, null);
        // reset vuex mapPromise to null (ie, "no map")
        this.$store.dispatch(actions.SET_MAP_PROMISE, null);
        next();
    },
    async beforeCreate() {
        this.$store.dispatch(actions.ADD_LOADING, 'load stations page');
        this.$store.dispatch(actions.SET_MAP_SOURCE_DATA_LOADED, false);
        // update the mapLoaded promise in vuex
        const mapLoadedPromise = new Promise((resolve, reject) => {
            this.$on('mapLoaded', (map) => {
                resolve(map);
            });
            this.$on('mapLoadError', (err) => {
                reject(err);
            });
        });
        this.$store.dispatch(actions.SET_MAP_PROMISE, mapLoadedPromise);
    },
    async mounted() {
        // get geojson lookup tables
        // this has to happen before anything can be done with the map/data
        await this.getLookupTables();
        // define map layers array
        this.mapLayers = this.getMapLayersArray();

        // add load event handlers
        // before the sources and layers are added
        await this.addLoadEventHandlers();

        // initialize map sources & layers
        await this.addLayers(this.$cfg.mapboxgl.baseStyle);
        this.mapLayersReady = true;

        // set up map when station data becomes available
        // @TODO: ensure this happens only once per map load,
        // ...and in order
        await this.initTooltip();
        await this.addLayerEventHandlers();
        await this.addViewEventHandlers();
        // if vuex has a new map state queued
        await this.loadStateFromVuex();
        // initialize map filters
        await this.initFilters();
    },
    methods: {
        /**
         * process any state items queued in the "loadState" module
         * then reset all items to null
         */
        async loadStateFromVuex() {
            const loadState = this.$store.state.loadState;
            const map = await this.mapPromise;

            if (loadState.mapCenter) {
                map.setCenter(loadState.mapCenter);
            }

            if (loadState.mapZoom) {
                map.setZoom(loadState.mapZoom);
            }

            if (loadState.mapFilterSettings) {
                this.$store.dispatch(actions.SET_FILTER_SETTINGS, loadState.mapFilterSettings);
            }

            // @NOTE: this will bring up the sidebar with the station title
            // even if it's on the wrong page. As long as the payload of the loadState
            // includes the correct page path, that shouldn't happen.
            if (loadState.selectFeature && loadState.selectFeature.type === 'station') {
                const { id, coords } = loadState.selectFeature;
                this.selectStation({
                    properties: { id },
                    geometry: { coordinates: coords },
                });
            }

            // clear loadState module
            this.$store.dispatch(actions.RESET_LOADSTATE_ITEMS);
        },
        /**
         * get an array of map layer configs
         * @return {Array} formatted config array
         */
        getMapLayersArray() {
            // marker layer ids (so we can do loop operations on all layers)
            return [
                {
                    type: 'stations',
                    sourceId: 'stations-data',
                    color: [
                        'match',
                        ['get', 'ty'],
                        this.lookupPropertyByValue('ty', 'Historical'), String(appColors.markerHistorical),
                        this.lookupPropertyByValue('ty', 'Current'), String(appColors.markerCurrent),
                        /* other */ '#ccc',
                    ],
                    pointLayer: 'stations-points',
                    /**
                     * callback for map sourcedata event
                     * @param  {Object} event mapbox gl sourcedata event
                     */
                    sourceCallback: () => {
                        const source = this.map.getSource('stations-data');
                        if (source && this.map.isSourceLoaded('stations-data')) {
                            this.$store.dispatch(actions.SET_MAP_SOURCE_DATA_LOADED, true);
                        }
                    },
                },
            ];
        },
        /**
         * add user-defined map sources and layers
         * @param  {String}  styleUrl mapbox style URL (for ordering layers)
         */
        async addLayers(styleUrl) {
            const labelLayerId = labelLayer(styleUrl);

            // add study area mask [below labels, if any]
            await this.addStudyArea(labelLayerId);
            // initialize point layer(s)
            await this.addPointLayers(labelLayerId);
        },
        /**
         * show tooltip with given html and coordinates
         * @param  {mapboxgl.LngLat} coordinates LngLatLike coordinates
         * @param  {String} html        tooltip HTML content
         */
        showTooltip(coordinates, html) {
            const el = this.tooltipMarker.getElement();
            this.tooltipMarker.setLngLat(coordinates);
            el.innerHTML = html;
            el.classList.remove('hidden');
        },
        /**
         * hide the map tooltip
         */
        hideTooltip() {
            const el = this.tooltipMarker.getElement();
            el.classList.add('hidden');
        },
        /**
         * add study area mask polygon
         * @param  {String}  layerAbove layer id to go stack new layers (e.g. labels)
         */
        async addStudyArea(layerAbove) {
            const map = await this.mapPromise;

            // add study area mask
            map.addSource('bcwt-study-area', {
                type: 'geojson',
                data: '/static/bcwt_studyarea_split.geojson',
            });
            map.addLayer({
                id: 'bcwt-study-area',
                type: 'fill',
                source: 'bcwt-study-area',
                paint: {
                    'fill-color': String(appColors.studyAreaMask),
                    'fill-opacity': 0.65,
                },
            });

            // move the layer below layerAbove, if specified
            if (layerAbove) {
                map.moveLayer('bcwt-study-area', layerAbove);
            }
        },
        /**
         * add mapbox sources and layers
         */
        async addPointLayers(layerAbove) {
            this.$store.dispatch(actions.ADD_LOADING, 'add map sources');
            const map = await this.mapPromise;

            // add split-up sources
            this.mapLayers.forEach(({ sourceId }) => {
                // reset map source data loaded var
                this.$store.dispatch(actions.SET_MAP_SOURCE_DATA_LOADED, false);
                map.addSource(sourceId, {
                    type: 'geojson',
                    data: `${this.mapSourceUrl}/all.geojson`,
                    buffer: 0,
                });
            });

            // unclustered points layers
            this.mapLayers.forEach(({ pointLayer, sourceId, color }) => {
                map.addLayer({
                    id: pointLayer,
                    type: 'circle',
                    source: sourceId,
                    paint: {
                        'circle-color': color,
                        // make circles larger as the user zooms in
                        'circle-radius': [
                            'interpolate', ['linear'], ['zoom'],
                            1, 2,
                            18, 6,
                        ],
                        'circle-stroke-width': [
                            'interpolate', ['linear'], ['zoom'],
                            1, 1,
                            18, 2,
                        ],
                        'circle-stroke-color': String(appColors.markerStroke),
                    },
                });

                // move the layer below layerAbove, if specified
                if (layerAbove) {
                    map.moveLayer(pointLayer, layerAbove);
                }
            });

            // add non-interactive layers to highlight the selected marker
            map.addLayer({
                id: 'selected-point-bg',
                type: 'circle',
                source: this.getMapLayersArray()[0].sourceId,
                // a green circle to appear beneath the selected feature
                paint: {
                    'circle-color': String(appColors.markerSelected),
                    // make circles larger as the user zooms in
                    'circle-radius': [
                        'interpolate', ['linear'], ['zoom'],
                        1, 12,
                        18, 16,
                    ],
                    'circle-opacity': 0,
                },
            });
            map.addLayer({
                id: 'selected-point',
                type: 'circle',
                source: this.getMapLayersArray()[0].sourceId,
                // same as the selected feature, but slightly larger
                paint: {
                    'circle-color': this.getMapLayersArray()[0].color,
                    // make circles larger as the user zooms in
                    'circle-radius': [
                        'interpolate', ['linear'], ['zoom'],
                        1, 4,
                        18, 8,
                    ],
                    'circle-stroke-width': 2,
                    'circle-opacity': 0,
                    'circle-stroke-opacity': 0,
                    'circle-stroke-color': String(appColors.markerStroke),
                },
            });

            // re-set highlighted marker (if any)
            if (this.selectedStation) {
                this.updateSelectedMarker(this.selectedStation.id);
            }
            this.$store.dispatch(actions.REMOVE_LOADING, 'add map sources');
        },
        /**
         * return an array of all interactive feature layer ids
         * @return {Array} layer id strings
         */
        getInteractiveLayers() {
            const featureLayers = [];
            this.mapLayers.forEach((l) => {
                featureLayers.push(l.pointLayer);
            });
            return featureLayers;
        },
        /**
         * map loaded callback
         * @param  {mapboxgl.Map} map map object
         */
        mapLoaded(map) {
            this.map = map;

            // add navigation controls
            map.addControl(new mapboxgl.NavigationControl({
                // hide rotation control
                showCompass: false,
            }), 'top-left');
            // add scale control
            map.addControl(new mapboxgl.ScaleControl({
                maxWidth: 75,
            }));

            // fulfill promise
            this.$emit('mapLoaded', map);
            this.$store.dispatch(actions.REMOVE_LOADING, 'load stations page');
        },
        /**
         * initialize tooltip object
         * to be controlled with showTooltip, hideTooltip
         */
        async initTooltip() {
            const map = await this.mapPromise;
            const el = document.createElement('div');
            el.classList.add('tooltip-marker');
            el.classList.add('hidden');

            this.tooltipMarker = new mapboxgl.Marker(el, {
                offset: [72, 0],
            })
            .setLngLat(map.getCenter())
            .addTo(map);
        },
        /**
         * bind listeners for map loading events
         */
        async addLoadEventHandlers() {
            const map = await this.mapPromise;
            // bind event listener to track when the source is loaded
            this.mapLayers.forEach(({ sourceCallback }) => {
                map.on('sourcedata', sourceCallback);
            });
        },
        /**
         * bind event handlers to map features
         */
        async addLayerEventHandlers() {
            const map = await this.mapPromise;

            // change mouse cursor on mouseover
            this.getInteractiveLayers().forEach((layerId) => {
                map.on('mousemove', layerId, () => {
                    map.getCanvas().style.cursor = 'pointer';
                });
                map.on('mouseleave', layerId, () => {
                    map.getCanvas().style.cursor = '';
                });
            });

            // show tooltip on mouseover
            this.mapLayers.forEach(({ pointLayer }) => {
                map.on('mousemove', pointLayer, (ev) => {
                    const feature = ev.features[0];
                    const featureCoords = feature.geometry.coordinates;
                    // show tooltip with some useful properties
                    this.showTooltip(featureCoords, `${feature.properties.name}`);
                });
                map.on('mouseleave', pointLayer, () => {
                    this.hideTooltip();
                });

                // update sidebar on marker click
                map.on('click', (ev) => {
                    // check for fetures at click location
                    if (ev.point) {
                        const markerFeatures = this.map.queryRenderedFeatures(ev.point, {
                            layers: this.getInteractiveLayers(),
                        });
                        if (markerFeatures.length > 0) {
                            // show the marker disambiguator if there are more than one
                            if (markerFeatures.length > 1) {
                                this.overlapFeatures = markerFeatures;
                                this.showOverlapPicker = true;
                                return;
                            }

                            // if only one feature was clicked, select it
                            this.selectStation(markerFeatures[0]);
                        }
                    }
                    // don't do anything for bare map clicks
                });
            });

            // track map zoom level
            map.on('zoom', () => {
                this.mapZoom = map.getZoom();
            });
        },
        /**
         * update the map layer paint properties to highlight the selected feature
         * @param  {String}  markerId marker ID to highlight
         */
        async updateSelectedMarker(markerId) {
            const map = await this.mapPromise;
            // show the selected marker in the "selected-point" layer
            map.setPaintProperty('selected-point', 'circle-opacity', [
                'case',
                ['==', ['get', 'id'], markerId],
                1,
                0,
            ]);
            map.setPaintProperty('selected-point', 'circle-stroke-opacity', [
                'case',
                ['==', ['get', 'id'], markerId],
                1,
                0,
            ]);
            // show the green highlight underneath the selected marker
            map.setPaintProperty('selected-point-bg', 'circle-opacity', [
                'case',
                ['==', ['get', 'id'], markerId],
                0.5,
                0,
            ]);
        },
        /**
         * bind event handlers to map view events
         * (zoom, pan, etc.)
         */
        async addViewEventHandlers() {
            const map = await this.mapPromise;

            // update bounding box in vuex
            map.on('moveend', () => this.calculateBounds());
            this.calculateBounds(); // initial run

            // save map position in localstorage
            map.on('moveend', () => {
                this.saveMapPosition('bcwt');
            });

            // save map position in vuex
            map.on('moveend', this.setMapStatePosition);

            // show context menu on right mouse click
            map.on('contextmenu', this.showContextMenu);
        },
        /**
         * calculate the viewport bounding box and update vuex
         */
        async calculateBounds() {
            const map = await this.mapPromise;
            const bounds = map.getBounds().toArray();
            // convert to bbox format for turf
            const bbox = [].concat(...bounds);
            this.$store.dispatch(actions.SET_MAP_BOUNDS, bbox);
        },
        /**
        * create and return a Mapbox GL popup
        * @param  {mapboxgl.LngLat} lngLat     LngLatLike coordinates
        * @param  {Object} vComponent Vue component for the popup contents
        * @param  {Object} propsData  props to pass to the vue component
        * @return {mapboxgl.Popup}            Map popup object
        */
        createMapPopup(lngLat, vComponent, propsData) {
            // create (empty) map popup
            const mapboxPopup = new mapboxgl.Popup({
                closeButton: false, // we'll use a custom close button
            })
            .setLngLat(lngLat)
            .setHTML('<div class="click-popup"></div>')
            .addTo(this.map);

            // manually mount Popup component to the empty map popup
            const popupContentEl = mapboxPopup._content.querySelector('.click-popup'); // eslint-disable-line
            const PopupConstructor = Vue.extend(vComponent);
            const PopupComponent = new PopupConstructor({
                store: this.$store,
                propsData,
            }).$mount(popupContentEl);

            // remove mapbox popup on close event
            PopupComponent.$on('close', () => {
                mapboxPopup.remove();
            });

            // destroy Popup component on popup close
            // (happens any time a map popup is removed
            // including a new popup taking its place
            // and manual close events) (see: "closeOnClick")
            mapboxPopup.on('close', () => {
                PopupComponent.$destroy();
            });

            return mapboxPopup;
        },
        showContextMenu({ lngLat }) {
            if (this.contextMenu) {
                this.contextMenu.remove();
            }
            this.contextMenu = this.createMapPopup(lngLat, MapContextMenu, {
                lngLat,
            });
        },
        /**
         * return (non-reactive) Mapbox GL constructor options
         * @return {Object} map options
         */
        getMapOptions() {
            return {
                style: this.$cfg.mapboxgl.baseStyle,
                center: this.loadMapPosition('bcwt').center || [-121.2, 50.9],
                zoom: this.loadMapPosition('bcwt').zoom || 5.4,
                attributionControl: false,
            };
        },
        /**
         * set the selected station feature
         * @param  {Object} station geojson feature from map query
         */
        selectStation(station) {
            window.gtag('event', 'Station', { action: 'select', label: 'User selected a station' });
            const id = station.properties.id;
            const coords = station.geometry.coordinates;
            this.$store.dispatch(actions.SELECT_STATION, {
                id,
                coords,
            });
            this.$store.dispatch(actions.SHOW_MAP_SIDEBAR, true);
        },
        async initFilters() {
            const filterOptions = await this.getFilterOptions();
            this.$store.dispatch(actions.SET_FILTER_OPTIONS, filterOptions);
            this.$store.dispatch(actions.SET_LOADED_FILTERS, this.filters);
        },
        async getFilterOptions() {
            this.$store.dispatch(actions.ADD_LOADING, 'get station filters');
            try {
                const { data } = await apolloClient.query({
                    query: gql`query ($type: StationsFilterType!) {
                    getStationFilters(type: $type)
                }`,
                    variables: {
                        type: this.stationType,
                    },
                });
                return data.getStationFilters;
            } catch (ex) {
                this.$store.dispatch(actions.TRIGGER_TOAST, 'Error retrieving station filters');
            } finally {
                this.$store.dispatch(actions.REMOVE_LOADING, 'get station filters');
            }
            return [];
        },
        async updateMapFilters() {
            const settings = this.filterSettings;
            const mapboxFilters = ['all'];
            const map = await this.mapPromise;

            let isEmpty = false;
            Object.values(settings).forEach((item) => {
                if (Array.isArray(item) && item.length === 0) {
                    isEmpty = true;
                }
            });
            if (isEmpty) {
                map.setFilter('stations-points', ['has', 'any']);
                return;
            }

            // network
            if (settings.network) {
                const expressions = settings.network.map((value) => {
                    const valueId = this.lookupPropertyByValue('net', value);
                    return ['==', ['get', 'net'], valueId];
                });
                if (expressions.length > 0) {
                    mapboxFilters.push([
                        'any',
                        ...expressions,
                    ]);
                } else { // if no options are selected
                    // something to select 0 features
                    mapboxFilters.push(['!has', 'net']);
                }
            }

            // analyses
            if (settings.analyses) {
                const expressions = settings.analyses.map((value) => {
                    const valueString = value.toString();
                    const ex = ['==', ['has', valueString, ['object', ['get', 'analysesObj']]], true];
                    return ex;
                });
                if (expressions.length > 0) {
                    mapboxFilters.push([
                        'any',
                        ...expressions,
                    ]);
                } else { // if no options are selected
                    // something to select 0 features
                    mapboxFilters.push(['!has', 'analyses']);
                }
            }

            // status
            if (settings.status) {
                const expressions = settings.status.map((value) => {
                    const valueId = this.lookupPropertyByValue('ty', value);
                    return ['==', ['get', 'ty'], valueId];
                });
                if (expressions.length > 0) {
                    mapboxFilters.push([
                        'any',
                        ...expressions,
                    ]);
                } else { // if no options are selected
                    // something to select 0 features
                    mapboxFilters.push(['!has', 'ty']);
                }
            }

            // yearMin
            if (settings.yearMin) {
                // station[yearMax] >= filter[yearMin]
                const setting = settings.yearMin.value;
                const expression = [
                    '>=',
                    [
                        'to-number', // cast as number
                        ['at', 1, ['get', 'yr']], // yr[1] (yearMax)
                    ],
                    setting,
                ];
                mapboxFilters.push(expression);
            }

            // yearMax
            if (settings.yearMax) {
                // station[yearMin] <= filter[yearMax]
                const setting = settings.yearMax.value;
                const expression = [
                    '<=',
                    [
                        'to-number', // cast as number
                        ['at', 0, ['get', 'yr']], // yr[0] (yearMin)
                    ],
                    setting,
                ];
                mapboxFilters.push(expression);
            }

            // "area"
            if (settings.area) {
                const expressions = settings.area.map((value) => {
                    const range = value.split('-');
                    const [min, max] = range.map(v => Number(v));
                    return [
                        'all',
                        // always filter out nulls if a value is set
                        ['!=', null, ['get', 'area']],
                        [
                            '>=',
                            [
                                'to-number', // cast to number
                                ['get', 'area'],
                            ],
                            min,
                        ],
                        [
                            '<=',
                            [
                                'to-number', // cast to number
                                ['get', 'area'],
                            ],
                            max,
                        ],
                    ];
                });
                if (expressions.length > 0) {
                    mapboxFilters.push([
                        'any',
                        ...expressions,
                    ]);
                } else { // if no options are selected
                    // something to select 0 features
                    mapboxFilters.push(['!has', 'area']);
                }
            }

            map.setFilter('stations-points', mapboxFilters);
        },
        /**
         * hide all map tools, optionally skipping one
         * to leave it open
         * @param  {String} skip variable name to skip
         */
        hideMapTools(skip) {
            let vars = [
                'showMapLegend',
                'showStyleSwitcher',
            ];
            if (skip) {
                vars = vars.filter(e => e !== skip);
            }
            vars.forEach((e) => {
                this[e] = false;
            });
        },
    },
    watch: {
        // hide tooltip on zoom
        mapZoom() {
            this.hideTooltip();
        },
        // focus on newly-selected stations
        selectedStation(station) {
            if (station) {
                this.revealFeature(station);
                this.updateSelectedMarker(station.id);
            }
        },
        // update mapbox filters when filter settings change
        filterSettings() {
            this.updateMapFilters();
        },
        // when a map tool is shown, hide all the others
        showMapLegend(val) {
            if (val) {
                this.hideMapTools('showMapLegend');
            }
        },
        // when a map tool is shown, hide all the others
        showStyleSwitcher(val) {
            if (val) {
                this.hideMapTools('showStyleSwitcher');
            }
        },
    },
};



// WEBPACK FOOTER //
// ./src/mixins/stationsPage.js